import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { useSelector } from "react-redux";

ChartJS.register(
  BarController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
);

const CARD_IDS = [
  "invoiceByLocation",
  "salesCreditNote",
  "invoiceCountByLocation",
];

const COLORS = ["#5CC7C1", "#5C74C7"];
const DONUT_COLORS = [
  "#56c2bf",
  "#4ea3d8",
  "#5d74c9",
  "#42b3d0",
  "#ef6b47",
  "#48c799",
  "#f07cb1",
  "#4e535b",
  "#f0cb38",
  "#9da0e3",
  "#62b9df",
  "#66c58a",
];

function getLocaleBySettings(priceFormat = {}) {
  if (priceFormat?.currency === "INR") return "en-IN";
  if (priceFormat?.thousand_separator === ".") return "nl-NL";
  return "en-US";
}

function getScaleConfig(priceFormat = {}) {
  const s = String(priceFormat?.show_amount_in || "Thousand").toLowerCase();
  if (s.includes("billion"))
    return { divisor: 1000000000, unitLabel: "Billion" };
  if (s.includes("million")) return { divisor: 1000000, unitLabel: "Million" };
  if (s.includes("lakh")) return { divisor: 100000, unitLabel: "Lakh" };
  if (s.includes("crore")) return { divisor: 10000000, unitLabel: "Crore" };
  return { divisor: 1000, unitLabel: "Thousand" };
}

function formatAxisNumber(n, priceFormat = {}) {
  const locale = getLocaleBySettings(priceFormat);
  const decimal = Number(priceFormat?.decimal_point || 2);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimal,
  }).format(n);
}

function makeStepTicks(maxVal, priceFormat = {}, divisor = 1000) {
  if (!maxVal || maxVal <= 0) return {};
  const raw = maxVal / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = Math.ceil(raw / magnitude) * magnitude;
  const max = Math.ceil(maxVal / step) * step;
  return {
    min: 0,
    max,
    ticks: {
      stepSize: step,
      callback: (v) => formatAxisNumber(Number(v) / divisor, priceFormat),
    },
  };
}

function sumByMonth(byMonth = {}) {
  return Object.values(byMonth).reduce(
    (acc, m) => ({
      invoices: acc.invoices + (Number(m?.invoices) || 0),
      qty: acc.qty + (Number(m?.qty) || 0),
      sales: acc.sales + (Number(m?.sales) || 0),
    }),
    { invoices: 0, qty: 0, sales: 0 },
  );
}

function rowsFromGroups(groups = []) {
  const salesGroup =
    groups.find(
      (g) => /sales/i.test(g.label) && !/exchange|refund|credit/i.test(g.label),
    ) || groups[0];
  const refundGroup = groups.find((g) =>
    /exchange|refund|credit/i.test(g.label),
  );
  const map = new Map();

  const add = (children = [], k) => {
    children.forEach((c) => {
      const rec = map.get(c.label) || {
        location: c.label,
        invoiceAmount: 0,
        creditAmount: 0,
        invoiceQty: 0,
        creditQty: 0,
        invoiceCount: 0,
      };
      const t = sumByMonth(c.byMonth);
      if (k === "invoice") {
        rec.invoiceAmount = t.sales;
        rec.invoiceQty = t.qty;
        rec.invoiceCount = t.invoices;
      } else {
        rec.creditAmount = t.sales;
        rec.creditQty = t.qty;
      }
      map.set(c.label, rec);
    });
  };

  add(salesGroup?.children, "invoice");
  add(refundGroup?.children, "credit");
  return [...map.values()].map((r) => ({
    ...r,
    totalAmount: r.invoiceAmount + r.creditAmount,
    totalQty: r.invoiceQty + r.creditQty,
  }));
}

function sliceRows(rows, topN, key) {
  const n = parseInt(topN, 10);
  if (Number.isNaN(n) || n <= 0) return rows;
  return [...rows]
    .sort((a, b) => Number(b[key] || 0) - Number(a[key] || 0))
    .slice(0, n);
}

function TopNDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div style={styles.ddWrap} ref={ref}>
      <div style={styles.ddBtn} onClick={() => setOpen((v) => !v)}>
        Top {value} ▾
      </div>
      {open && (
        <div style={styles.ddMenu}>
          {["10", "20", "30"].map((x) => (
            <div
              key={x}
              style={styles.ddOpt}
              onClick={() => {
                onChange(x);
                setOpen(false);
              }}
            >
              Top {x}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ByTypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div style={styles.ddWrap} ref={ref}>
      <div style={styles.ddBtn} onClick={() => setOpen((v) => !v)}>
        By {value} ▾
      </div>
      {open && (
        <div style={styles.ddMenu}>
          {["Amount", "Qty"].map((x) => (
            <div
              key={x}
              style={styles.ddOpt}
              onClick={() => {
                onChange(x);
                setOpen(false);
              }}
            >
              {x}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ThreeDotsMenu({ chartRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handlePrint = () => {
    window.print();
    setOpen(false);
  };
  const handleDownload = () => {
    const chart = chartRef?.current;
    if (!chart) return;
    const url = chart.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.png";
    link.click();
    setOpen(false);
  };

  return (
    <div style={styles.menuWrapper} ref={ref}>
      <div style={styles.dots} onClick={() => setOpen(!open)}>
        ⋮
      </div>
      {open && (
        <div style={styles.menu}>
          <div style={styles.menuIconRow}>
            <div style={styles.menuIconBtn} onClick={handlePrint} title="Print">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </div>
            <div
              style={styles.menuIconBtn}
              onClick={handleDownload}
              title="Download"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M19 16 v3 M17.5 19 h3" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Header({
  title,
  topN,
  setTopN,
  byType,
  setByType,
  chartRef,
  onDragStart,
  onDragEnd,
  showTop = true,
  showByType = true,
}) {
  return (
    <div style={styles.head}>
      <div
        style={styles.drag}
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        title="Drag to reorder chart"
      >
        <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
          <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
          <circle cx="3" cy="6" r="1" fill="#6B7280" />
          <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
          <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
          <circle cx="9" cy="6" r="1" fill="#6B7280" />
          <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
        </svg>
      </div>
      <p style={styles.title}>{title}</p>
      <div style={styles.right}>
        {showTop && <TopNDropdown value={topN} onChange={setTopN} />}
        {showByType && <ByTypeDropdown value={byType} onChange={setByType} />}
        <ThreeDotsMenu chartRef={chartRef} />
      </div>
    </div>
  );
}

export default function InvoiceChart({ groups = [] }) {
  const priceFormat = useSelector(
    (s) => s?.app?.organisation_settings?.price_format || {},
  );
  const rows = useMemo(() => rowsFromGroups(groups), [groups]);

  const [order, setOrder] = useState(CARD_IDS);
  const [dragging, setDragging] = useState(null);
  const [f, setF] = useState({
    invoiceByLocation: { topN: "10", byType: "Amount" },
    salesCreditNote: { topN: "10", byType: "Amount" },
    invoiceCountByLocation: { topN: "10", byType: "Amount" },
  });

  // Hooks must be called unconditionally at top level — not inside objects
  const barRef = useRef();
  const typeRef = useRef();
  const locRef = useRef();

  const { divisor, unitLabel } = getScaleConfig(priceFormat);
  const currency = priceFormat?.currency || "THB";

  const barRows = useMemo(
    () =>
      sliceRows(
        rows,
        f.invoiceByLocation.topN,
        f.invoiceByLocation.byType === "Amount" ? "totalAmount" : "totalQty",
      ),
    [rows, f.invoiceByLocation],
  );
  const barData = useMemo(
    () => ({
      labels: barRows.map((r) => r.location),
      datasets: [
        {
          label: "Invoice",
          data: barRows.map((r) =>
            f.invoiceByLocation.byType === "Amount"
              ? r.invoiceAmount
              : r.invoiceQty,
          ),
          backgroundColor: COLORS[0],
          borderRadius: 0,
          barThickness: 8,
        },
        {
          label: "Credit Note",
          data: barRows.map((r) =>
            f.invoiceByLocation.byType === "Amount"
              ? r.creditAmount
              : r.creditQty,
          ),
          backgroundColor: COLORS[1],
          borderRadius: 0,
          barThickness: 8,
        },
      ],
    }),
    [barRows, f.invoiceByLocation.byType],
  );
  const barMax = Math.max(
    ...barData.datasets.flatMap((d) => d.data.map((x) => Number(x || 0))),
    0,
  );
  const barOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rectRounded",
          boxWidth: 8,
          boxHeight: 8,
          color: "#6b7280",
          padding: 12,
          font: { size: 10 },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90,
          font: { size: 10 },
          color: "#7b7b7b",
        },
      },
      y: {
        beginAtZero: true,
        ...makeStepTicks(
          barMax,
          f.invoiceByLocation.byType === "Amount"
            ? priceFormat
            : { decimal_point: 0 },
          f.invoiceByLocation.byType === "Amount" ? divisor : 1,
        ),
        grid: { color: "rgba(0,0,0,0.08)", drawBorder: false },
        title: {
          display: true,
          text:
            f.invoiceByLocation.byType === "Amount"
              ? `${currency} (${unitLabel})`
              : "Qty",
          color: "#4b5563",
          font: { size: 12, weight: 600 },
        },
      },
    },
  };

  const typeRows = useMemo(
    () =>
      sliceRows(
        rows,
        f.salesCreditNote.topN,
        f.salesCreditNote.byType === "Amount" ? "totalAmount" : "totalQty",
      ),
    [rows, f.salesCreditNote],
  );
  const typeData = {
    labels: ["Invoice", "Exchange & Refund"],
    datasets: [
      {
        data: [
          typeRows.reduce(
            (a, r) =>
              a +
              (f.salesCreditNote.byType === "Amount"
                ? r.invoiceAmount
                : r.invoiceQty),
            0,
          ),
          typeRows.reduce(
            (a, r) =>
              a +
              (f.salesCreditNote.byType === "Amount"
                ? r.creditAmount
                : r.creditQty),
            0,
          ),
        ],
        backgroundColor: ["#5CC7C1", "#f0cb38"],
        borderWidth: 0,
      },
    ],
  };

  const locRows = useMemo(
    () =>
      sliceRows(
        rows,
        f.invoiceCountByLocation.topN,
        f.invoiceCountByLocation.byType === "Amount"
          ? "totalAmount"
          : "invoiceCount",
      ),
    [rows, f.invoiceCountByLocation],
  );
  const locData = {
    labels: locRows.map((r) => r.location),
    datasets: [
      {
        data: locRows.map((r) =>
          f.invoiceCountByLocation.byType === "Amount"
            ? r.totalAmount
            : r.invoiceCount,
        ),
        backgroundColor: locRows.map(
          (_, i) => DONUT_COLORS[i % DONUT_COLORS.length],
        ),
        borderWidth: 0,
      },
    ],
  };

  const donutOpts = (byType) => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          color: "#7b7b7b",
          padding: 10,
          font: { size: 10 },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = Number(ctx.raw || 0);
            const total =
              (ctx.dataset.data || []).reduce(
                (a, b) => a + Number(b || 0),
                0,
              ) || 1;
            const pct = ((val * 100) / total).toFixed(2);
            const shown =
              byType === "Amount"
                ? formatAxisNumber(val, priceFormat)
                : val.toLocaleString();
            return `${ctx.label}: ${shown} (${pct}%)`;
          },
        },
      },
    },
  });

  const setFilter = (k, key, value) =>
    setF((p) => ({ ...p, [k]: { ...p[k], [key]: value } }));

  const drop = (target) => {
    if (!dragging || dragging === target) return;
    const next = [...order];
    const from = next.indexOf(dragging);
    const to = next.indexOf(target);
    if (from < 0 || to < 0) return;
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setOrder(next);
    setDragging(null);
  };

  if (!rows.length)
    return <div style={styles.empty}>No chart data available.</div>;

  const cards = {
    invoiceByLocation: (
      <div style={styles.card}>
        <Header
          title="Invoice by location"
          topN={f.invoiceByLocation.topN}
          setTopN={(v) => setFilter("invoiceByLocation", "topN", v)}
          byType={f.invoiceByLocation.byType}
          setByType={(v) => setFilter("invoiceByLocation", "byType", v)}
          chartRef={barRef}
          onDragStart={() => setDragging("invoiceByLocation")}
          onDragEnd={() => setDragging(null)}
        />
        <div style={styles.chart}>
          <Bar ref={barRef} data={barData} options={barOpts} />
        </div>
      </div>
    ),
    salesCreditNote: (
      <div style={styles.card}>
        <Header
          title="Sales and Credit Note"
          topN={f.salesCreditNote.topN}
          setTopN={(v) => setFilter("salesCreditNote", "topN", v)}
          byType={f.salesCreditNote.byType}
          setByType={(v) => setFilter("salesCreditNote", "byType", v)}
          showTop={false}
          showByType={false}
          chartRef={typeRef}
          onDragStart={() => setDragging("salesCreditNote")}
          onDragEnd={() => setDragging(null)}
        />
        <div style={styles.chart}>
          <Doughnut
            ref={typeRef}
            data={typeData}
            options={donutOpts(f.salesCreditNote.byType)}
          />
        </div>
      </div>
    ),
    invoiceCountByLocation: (
      <div style={{ ...styles.card}}>
        <Header
          title="No. of Invoice by Location"
          topN={f.invoiceCountByLocation.topN}
          setTopN={(v) => setFilter("invoiceCountByLocation", "topN", v)}
          byType={f.invoiceCountByLocation.byType}
          setByType={(v) => setFilter("invoiceCountByLocation", "byType", v)}
          chartRef={locRef}
          onDragStart={() => setDragging("invoiceCountByLocation")}
          onDragEnd={() => setDragging(null)}
        />
        <div style={styles.chart}>
          <Doughnut
            ref={locRef}
            data={locData}
            options={donutOpts(f.invoiceCountByLocation.byType)}
          />
        </div>
      </div>
    ),
  };

  return (
    <div style={styles.root}>
      <div style={styles.grid}>
        {order.map((id) => (
          <div
            key={id}
            style={styles.col}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drop(id)}
          >
            {cards[id]}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  root: { padding: "0 20px 16px", background: "#fff" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },
  col: { minWidth: 0, width: "100%" },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "10px 12px 8px",
    boxSizing: "border-box",
  },
  head: { display: "flex", alignItems: "center", gap: 8, marginBottom: 2 },
  right: { display: "flex", alignItems: "center", gap: 5, flexShrink: 0 },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "#606060",
    margin: "10px 2px",
    flex: 1,
    fontFamily: "Segoe UI",
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chart: { height: 440 },
  drag: {
    cursor: "grab",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "2px 3px",
    lineHeight: 0,
    flexShrink: 0,
  },
  ddWrap: { position: "relative", fontFamily: "Segoe UI" },
  ddBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#fff",
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  ddMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 130,
    zIndex: 20,
    overflow: "hidden",
  },
  ddOpt: {
    padding: "9px 14px",
    fontSize: 13,
    cursor: "pointer",
    color: "#606060",
  },
   menuWrapper: { position: "relative" },
  dots: {
    cursor: "pointer",
    fontSize: 22,
    color: "#606060",
    padding: "0 4px",
    lineHeight: 1,
    userSelect: "none",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 6px)",
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    zIndex: 30,
    padding: "10px 14px",
  },
  menuIconRow: {
    display: "flex",
    gap: 16,
    alignItems: "center",
  },
  menuIconBtn: {
    cursor: "pointer",
    padding: 6,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  empty: {
    margin: "0 20px",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: 24,
    textAlign: "center",
    color: "#6b7280",
    fontFamily: "Segoe UI",
  },
};
